import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { DaaruLogo } from "./branding/DaaruLogo";
import { Countdown } from "./Countdown";
import { formatDate, pickLocalized } from "./format";
import { RecentPapersTeaser } from "./RecentPapersTeaser";
import { useConferenceSettings } from "./useConferenceSettings";

export function LandingPage() {
  const { t, locale } = useLanguage();
  const { data: conf, loading, error } = useConferenceSettings();

  if (loading) return <p>{t.common.loading}</p>;
  if (error || !conf) return <div className="dn-alert dn-alert-error">{t.common.error}</div>;

  const org = pickLocalized(conf, locale, "name_en", "name_ar");
  const theme = pickLocalized(conf, locale, "theme_en", "theme_ar");
  const announcement =
    locale === "ar" ? conf.hero_announcement_ar : conf.hero_announcement_en;

  return (
    <>
      <section className="dn-hero">
        <div className="dn-hero-inner">
          <DaaruLogo size="hero" alt={org} />
          <h1>{t.conferenceEdition}</h1>
          <p className="theme">{theme}</p>
          <div className="meta">
            <span>
              {formatDate(conf.start_at, locale)} – {formatDate(conf.end_at, locale)}
            </span>
            <span>{pickLocalized(conf, locale, "venue_name_en", "venue_name_ar")}</span>
          </div>
          {announcement && <p className="theme">{announcement}</p>}
          <div className="dn-cta-row">
            <Link className="dn-btn dn-btn-gold" to="/register">
              {t.hero.ctaRegister}
            </Link>
            <Link className="dn-btn dn-btn-ghost" style={{ color: "#fff", borderColor: "rgba(255,255,255,0.45)" }} to="/submit-paper">
              {t.hero.ctaSubmit}
            </Link>
          </div>
          <Countdown startAt={conf.start_at} />
        </div>
      </section>

      <section className="dn-section" id="about">
        <h2>{t.sections.about}</h2>
        <p className="lead">{t.sections.aboutBody}</p>
        <div className="dn-arch">
          <h3>{t.sections.theme}</h3>
          <p style={{ fontSize: "1.25rem", fontFamily: "var(--dn-font-display)" }}>{theme}</p>
          {locale === "en" && <p className="dn-muted" dir="rtl">{conf.theme_ar}</p>}
          {locale === "ar" && <p className="dn-muted" dir="ltr">{conf.theme_en}</p>}
        </div>
      </section>

      <section className="dn-section" id="dates">
        <h2>{t.sections.dates}</h2>
        <div className="dn-grid">
          <div className="dn-card">
            <h3>{t.sections.abstractDeadline}</h3>
            <p>{formatDate(conf.abstract_deadline, locale)}</p>
          </div>
          <div className="dn-card">
            <h3>{t.sections.paperDeadline}</h3>
            <p>{formatDate(conf.paper_deadline, locale)}</p>
          </div>
          <div className="dn-card">
            <h3>{t.sections.conferenceDates}</h3>
            <p>
              {formatDate(conf.start_at, locale)} – {formatDate(conf.end_at, locale)}
            </p>
          </div>
          {conf.national_conference_date && (
            <div className="dn-card">
              <h3>{t.sections.nationalOptional}</h3>
              <p>{formatDate(conf.national_conference_date, locale)}</p>
            </div>
          )}
        </div>
      </section>

      <section className="dn-section">
        <h2>{t.sections.cfp}</h2>
        <p className="lead">{t.sections.cfpBody}</p>
        <Link className="dn-btn dn-btn-primary" to="/call-for-papers">
          {t.nav.cfp}
        </Link>
      </section>

      <section className="dn-section">
        <h2>{t.sections.guidelines}</h2>
        <ul>
          <li>{t.guidelines.languages}</li>
          <li>{t.guidelines.arabicFont}</li>
          <li>{t.guidelines.englishFont}</li>
          <li>{t.guidelines.frenchFont}</li>
          <li>{t.guidelines.format}</li>
        </ul>
      </section>

      <section className="dn-section">
        <h2>{t.sections.programme}</h2>
        <Link className="dn-btn dn-btn-ghost" to="/schedule">
          {t.nav.schedule}
        </Link>
      </section>

      <RecentPapersTeaser />

      <section className="dn-section">
        <h2>{t.sections.organizer}</h2>
        <div className="dn-grid">
          <div className="dn-card">
            <h3>{org}</h3>
            <p className="dn-muted">{t.sections.director}</p>
            <p>{pickLocalized(conf, locale, "director_name_en", "director_name_ar")}</p>
          </div>
          <div className="dn-card">
            <h3>{t.sections.contact}</h3>
            <p>
              <a href={`mailto:${conf.contact_email}`}>{conf.contact_email}</a>
            </p>
            <p>{conf.phone_primary}</p>
            {conf.phone_secondary && <p>{conf.phone_secondary}</p>}
          </div>
        </div>
      </section>

      <section className="dn-section dn-arch" style={{ textAlign: "center" }}>
        <h2>{t.sections.finalCta}</h2>
        <p className="lead" style={{ marginInline: "auto" }}>
          {t.sections.finalCtaBody}
        </p>
        <div className="dn-cta-row" style={{ justifyContent: "center" }}>
          <Link className="dn-btn dn-btn-primary" to="/register">
            {t.hero.ctaRegister}
          </Link>
          <Link className="dn-btn dn-btn-gold" to="/call-for-papers">
            {t.nav.cfp}
          </Link>
        </div>
      </section>
    </>
  );
}
